import React, { useState, useEffect } from 'react';
import { Form, Input, Row, Col, Button, Space, Modal } from 'antd';
import { apiRequest } from '../../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../../components/errorHandler';

interface CountryModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void; // Callback after successful submit
  initialValues?: {
    country_id?: number;
    country_name?: string;
  } | null;
  isEditMode?: boolean;
}

const CountryModal: React.FC<CountryModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  // Determine if submit button should be disabled
  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };

  // Handle form submit
  const handleAddCountry = async (values: any) => {
    try {
      const dataVal = {
        country_name: values.country_name,
        ...(initialValues?.country_id && { country_id: initialValues.country_id }),
      };

      const url = isEditMode ? 'edit-country' : 'add-country';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: dataVal,
      });

      onCreate(data); // Notify parent about success
      showSuccess(
        isEditMode
          ? 'Country updated successfully!'
          : 'Country created successfully!'
      );

      form.resetFields();

      if (isModalClose) {
        onCancel(); // Close modal if Submit button clicked
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} country`);
    }
  };

  // Reset or populate form when modal opens or mode changes
  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue({
        country_name: initialValues.country_name,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, isEditMode, initialValues, form]);

  return (
    <Modal
      title={isEditMode ? 'Edit Country' : 'Add New Country'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="50%"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleAddCountry}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="country_name"
              label={
                <span>
                  Country&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 255 characters)
                  </span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the Country' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input showCount maxLength={255} placeholder="e.g., United States" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
              >
                Submit & Create New Country
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CountryModal;
